import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  Lock,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  SkipForward,
  X,
} from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type {
  EnvironmentView,
  ReleaseDetail,
  ReleaseExecutionStep,
} from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  LoadingState,
  ProgressBar,
  StatusBadge,
} from "../../shared/ui";
import { CopyPlanFromPrevious } from "./CopyPlanFromPrevious";
import { ExecutionStepDrawer } from "./ExecutionStepDrawer";
import { StepOutcomeDrawer } from "./StepOutcomeDrawer";
import { useReleaseMutation } from "./useReleaseMutation";
import {
  EXECUTION_PHASE_LABELS,
  ON_FAILURE_LABELS,
  STEP_STATUS_LABELS,
  STEP_STATUS_TONE,
  formatDuration,
} from "./releaseVocabulary";

const PHASES = ["PREPARE", "DEPLOY", "VERIFY", "FINALISE"] as const;

/**
 * §37's execution table: the runbook, in order, as it is being worked through.
 *
 * <p>Grouped by phase and shown as a list rather than a stepper, because during a deployment
 * several people are working different steps at once and a single-highlighted-step control implies
 * a sequence that is not actually how the night goes.
 *
 * <p>A step waiting on another says so in words next to its start button, rather than the button
 * simply being dead. "Waiting for: take a database snapshot" tells somebody what to go and chase;
 * a greyed-out button tells them nothing.
 */
export function ReleaseExecutionTab({
  release,
  leg,
  onChanged,
}: {
  release: ReleaseDetail;
  /** The one leg of the journey being worked on, or null for the release as a whole (§63). */
  leg?: EnvironmentView | null;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [editing, setEditing] = useState<ReleaseExecutionStep | null>(null);
  const [adding, setAdding] = useState(false);
  const [outcome, setOutcome] = useState<{
    step: ReleaseExecutionStep;
    kind: "complete" | "fail" | "skip" | "retry";
  } | null>(null);
  const { run, saving } = useReleaseMutation(release.id);

  const steps = useQuery({
    queryKey: ["release-execution", release.id],
    queryFn: () =>
      api.get<ReleaseExecutionStep[]>(`/api/v1/releases/${release.id}/execution-steps`),
  });

  const rows = (steps.data ?? []).filter(
    (row) => !leg || row.environment == null || row.environment.id === leg.id,
  );

  /*
   * §37: reordering is only offered while nothing has been run. Once a step has started, the
   * numbers are the order things actually happened in, and renumbering them afterwards would
   * rewrite the account of the night rather than the plan for it.
   */
  const notStarted = rows.every((row) => row.status === "NOT_STARTED");

  const move = (index: number, by: number) => {
    const ordered = [...rows].sort((a, b) => a.sequenceNo - b.sequenceNo);
    const target = index + by;
    if (target < 0 || target >= ordered.length) {
      return;
    }
    const here = ordered[index];
    const there = ordered[target];
    if (!here || !there) {
      return;
    }
    ordered[index] = there;
    ordered[target] = here;
    void run(
      () =>
        api.put(`/api/v1/releases/${release.id}/execution-steps/order`, {
          stepIds: ordered.map((step) => step.id),
        }),
      onChanged,
    );
  };
  const settled = rows.filter(
    (row) => row.status === "COMPLETED" || row.status === "SKIPPED",
  ).length;

  const start = (step: ReleaseExecutionStep, override: boolean) =>
    void run(
      () =>
        api.post(`/api/v1/releases/${release.id}/execution-steps/${step.id}/start`, {
          override,
          lockVersion: step.lockVersion,
        }),
      onChanged,
    );

  return (
    <Card>
      <CardHeader
        title={leg ? `Change execution — ${leg.name}` : "Change execution"}
        subtitle={
          rows.length === 0
            ? "No steps planned yet."
            : `${settled} of ${rows.length} steps settled`
        }
        action={
          can("RELEASE_EDIT") && (
            <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
              Add step
            </Button>
          )
        }
      />
      <CardBody className="space-y-4">
        {rows.length > 0 && (
          <ProgressBar
            value={settled}
            total={rows.length}
            tone={settled === rows.length ? "green" : "blue"}
            label="Steps settled"
          />
        )}

        {steps.isPending ? (
          <LoadingState label="Loading the runbook…" />
        ) : rows.length === 0 ? (
          <>
            <EmptyState
              title="No runbook yet"
              message="What actually gets done during the deployment — snapshots, migrations, scaling, smoke tests. These belong to the release, not the backlog."
              action={
                can("RELEASE_EDIT") ? (
                  <Button onClick={() => setAdding(true)}>Add the first step</Button>
                ) : undefined
              }
            />
            {leg && (
              <CopyPlanFromPrevious release={release} leg={leg} onCopied={onChanged} />
            )}
          </>
        ) : (
          /*
           * §63: environment first, phase within it. A runbook is rarely one list — three steps
           * put a release on a sandbox and fifteen put it into production — and reading those
           * interleaved is how somebody runs a production step on the wrong night.
           */
          byEnvironment(rows).map(([environmentLabel, forEnvironment]) => (
          <div key={environmentLabel} className="space-y-3">
            {byEnvironment(rows).length > 1 && (
              <p className="border-b border-ink-200 pb-1 text-sm font-semibold text-ink-900">
                {environmentLabel}
              </p>
            )}
          {PHASES.filter((phase) => forEnvironment.some((row) => row.phase === phase))
            .map((phase) => (
            <div key={phase}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400">
                {EXECUTION_PHASE_LABELS[phase]}
              </p>
              <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                {forEnvironment
                  .filter((row) => row.phase === phase)
                  .map((row) => (
                    <li key={row.id} className="flex items-start gap-3 px-3 py-2.5">
                      <span className="tnum mt-0.5 w-6 shrink-0 text-xs text-ink-400">
                        {row.sequenceNo}
                      </span>
                      {can("RELEASE_EDIT") && notStarted && (
                        <span className="mt-0.5 flex shrink-0 flex-col gap-0.5">
                          <button
                            type="button"
                            aria-label={`Move ${row.title} earlier`}
                            className="text-ink-400 hover:text-ink-700 disabled:opacity-30"
                            disabled={saving || row.sequenceNo === 1}
                            onClick={() => move(row.sequenceNo - 1, -1)}
                          >
                            <ArrowUp className="h-3 w-3" aria-hidden />
                          </button>
                          <button
                            type="button"
                            aria-label={`Move ${row.title} later`}
                            className="text-ink-400 hover:text-ink-700 disabled:opacity-30"
                            disabled={saving || row.sequenceNo === rows.length}
                            onClick={() => move(row.sequenceNo - 1, 1)}
                          >
                            <ArrowDown className="h-3 w-3" aria-hidden />
                          </button>
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => can("RELEASE_EDIT") && setEditing(row)}
                          className="text-left text-sm font-medium text-ink-900 hover:underline"
                        >
                          {row.title}
                        </button>
                        <p className="mt-0.5 text-xs text-ink-500">
                          {row.stepType.toLowerCase()}
                          {row.owner && ` · ${row.owner.fullName}`}
                          {row.environment && ` · ${row.environment.name}`}
                          {row.component && ` · ${row.component}`}
                          {row.actualMinutes != null && ` · ${formatDuration(row.actualMinutes)}`}
                        </p>

                        {row.waitingFor.length > 0 && row.status === "NOT_STARTED" && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                            <Lock className="h-3 w-3" aria-hidden />
                            Waiting for {row.waitingFor.join(", ")}
                          </p>
                        )}

                        {row.failureReason && (
                          <p className="mt-1 rounded-md border-l-2 border-red-300 bg-red-50 px-2 py-1 text-xs text-red-800">
                            {row.failureReason}
                            {row.onFailureAction !== "CONTINUE" && (
                              <span className="mt-0.5 block text-red-700">
                                The plan says: {ON_FAILURE_LABELS[row.onFailureAction].toLowerCase()}.
                                Nothing has been done automatically.
                              </span>
                            )}
                          </p>
                        )}
                        {row.skipReason && (
                          <p className="mt-1 rounded-md bg-ink-50 px-2 py-1 text-xs text-ink-700">
                            Skipped — {row.skipReason}
                          </p>
                        )}
                        {row.evidence.length > 0 && (
                          <p className="mt-1 text-xs text-ink-500">
                            {row.evidence.length}{" "}
                            {row.evidence.length === 1 ? "piece" : "pieces"} of evidence
                          </p>
                        )}
                      </div>

                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <StatusBadge tone={STEP_STATUS_TONE[row.status]}>
                          {STEP_STATUS_LABELS[row.status]}
                        </StatusBadge>
                        {can("RELEASE_STEP_EXECUTE") && (
                          <div className="flex gap-1">
                            {/* The title has always opened this, which is not an affordance
                                anybody finds. Editing the wording of a step is as ordinary as
                                running it, and deserves a button that says so. */}
                            {can("RELEASE_EDIT") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<Pencil className="h-3.5 w-3.5" />}
                                aria-label={`Edit ${row.title}`}
                                onClick={() => setEditing(row)}
                              >
                                Edit
                              </Button>
                            )}
                            {(row.status === "NOT_STARTED" || row.status === "READY") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<Play className="h-3.5 w-3.5" />}
                                loading={saving}
                                title={
                                  row.waitingFor.length > 0
                                    ? `Waiting for ${row.waitingFor.join(", ")}`
                                    : undefined
                                }
                                onClick={() => start(row, row.waitingFor.length > 0)}
                              >
                                {row.waitingFor.length > 0 ? "Start anyway" : "Start"}
                              </Button>
                            )}
                            {row.status === "IN_PROGRESS" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  icon={<X className="h-3.5 w-3.5" />}
                                  onClick={() => setOutcome({ step: row, kind: "fail" })}
                                >
                                  Failed
                                </Button>
                                <Button
                                  size="sm"
                                  icon={<Check className="h-3.5 w-3.5" />}
                                  onClick={() => setOutcome({ step: row, kind: "complete" })}
                                >
                                  Done
                                </Button>
                              </>
                            )}
                            {row.status === "FAILED" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<RotateCcw className="h-3.5 w-3.5" />}
                                onClick={() => setOutcome({ step: row, kind: "retry" })}
                              >
                                Try again
                              </Button>
                            )}
                            {(row.status === "NOT_STARTED" || row.status === "READY"
                              || row.status === "BLOCKED") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<SkipForward className="h-3.5 w-3.5" />}
                                onClick={() => setOutcome({ step: row, kind: "skip" })}
                              >
                                Skip
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
          </div>
          ))
        )}

        {rows.some((row) => row.status === "FAILED") && (
          <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              A step failed. Nothing has been rolled back automatically — that is a decision for
              somebody to make, using the rollback plan.
            </span>
          </p>
        )}
      </CardBody>

      <ExecutionStepDrawer
        leg={leg}
        release={release}
        step={editing}
        open={adding || editing != null}
        allSteps={rows}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={onChanged}
      />
      <StepOutcomeDrawer
        release={release}
        step={outcome?.step ?? null}
        kind={outcome?.kind ?? "complete"}
        onClose={() => setOutcome(null)}
        onSaved={onChanged}
      />
    </Card>
  );
}

/**
 * Steps grouped by the environment they belong to, with the universal ones first.
 *
 * <p>A step with no environment is part of every deployment, which is the ordinary case and the
 * one to read first. Putting it under a heading would make the general case look special.
 */
function byEnvironment(
  rows: ReleaseExecutionStep[],
): [string, ReleaseExecutionStep[]][] {
  const groups = new Map<string, ReleaseExecutionStep[]>();
  for (const row of rows) {
    const label = row.environment?.name ?? "Every environment";
    groups.set(label, [...(groups.get(label) ?? []), row]);
  }
  return [...groups.entries()].sort(([a], [b]) =>
    a === "Every environment" ? -1 : b === "Every environment" ? 1 : a.localeCompare(b),
  );
}
